import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { NavBar } from "../components/navBar";
import Footer from "../components/Footer";
import { useUserStore } from "../stores/UserStore";
import { getProfile } from "../backend/api/auth";
import { User } from "../types/User";
import { getMyGearpals, deleteGearItem } from "../backend/api/gearlpal";
import { getMyLivepals } from "../backend/api/livepal";
import { LivePal, GearItem, GearPal } from "../types/Pal";
import { NotLogged } from "./Profilo/NotLogged";
import { Loading } from "./Profilo/Loading";
import { UserData } from "./Profilo/UserData";
import { Error } from "./Profilo/Error";
import { useUserAddress } from "../components/providers/ModalsProvider";
import UserAddress from "./Profilo/UserAddress";
import UserGearItems from "./Profilo/UserGearItems";
import UserVenues from "./Profilo/UserVenues";
import AddGearItemForm from "./Profilo/AddGearItemForm";
import AddLivePalForm from "./Profilo/AddLivePalForm";
import Tabs from "../components/Tabs";
import { HiOutlineMusicNote, HiOutlineHome } from "react-icons/hi";
import GearButton from "../ui/gearbutton";
import { useState, useCallback } from "react";
import GearHeadings from "../ui/gearheadings";
import EditGearItemModal from "../components/EditGearItemModal";
import AddOrEditPalForm from "../components/AddOrEditPalForm/AddOrEditPalForm";
import PalPopup from "../components/PalPopup/PalPopup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

const Profilo = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUserStore();
  const [activeTab, setActiveTab] = useState("gear");
  const [editingItem, setEditingItem] = useState<GearItem | null>(null);
  const [editingVenue, setEditingVenue] = useState<LivePal | null>(null);
  const [viewingPal, setViewingPal] = useState<GearPal | null>(null);
  const [viewingItem, setViewingItem] = useState<GearItem | null>(null);
  const queryClient = useQueryClient();
  const { open: openUserAddressModal } = useUserAddress();

  const { mutate: deleteItemMutation } = useMutation({
    mutationFn: (id: number) => deleteGearItem(id, i18n.language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gearpals"] });
      queryClient.invalidateQueries({ queryKey: ["my-gearpals"] });
      toast.success(t("item.deleted", "Articolo eliminato con successo"));
    },
    onError: () => {
      toast.error(
        t("item.deleteFailed", "Errore nell'eliminazione dell'articolo"),
      );
    },
  });

  const tabs = [
    {
      id: "gear",
      label: t("pages.profile.gearListing", "Gear Listing"),
      icon: <HiOutlineMusicNote />,
    },
    {
      id: "venue",
      label: t("pages.profile.venueListing", "Venue Listing"),
      icon: <HiOutlineHome />,
    },
  ];

  const { data: gearpalsData } = useQuery<GearPal[]>({
    queryKey: ["gearpals", i18n.language],
    queryFn: () => getMyGearpals(i18n.language),
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: livepalsData } = useQuery<LivePal[]>({
    queryKey: ["my-livepals", i18n.language],
    queryFn: () => getMyLivepals(i18n.language),
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const {
    data: profileData,
    isLoading,
    isError,
  } = useQuery<User>({
    queryKey: ["profile", i18n.language],
    queryFn: () => getProfile(i18n.language),
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (!user) {
    return <NotLogged />;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return <Error />;
  }

  const profile: User = profileData ?? user;

  const totalListingsCount =
    (gearpalsData?.length || 0) + (livepalsData?.length || 0);

  return (
    <div
      className="h-screen flex flex-col"
      style={{
        maxHeight: "100dvh",
        background:
          "linear-gradient(180deg, #1A0F2E 0%, #17092E 50%, #1A0F2E 100%)",
      }}
    >
      <NavBar />
      <main className="flex-grow pt-[70px] px-4 md:px-6 py-6 md:py-12 overflow-y-auto max-h-screen pb-[80px] md:pb-[70px]">
        <div className="max-w-[864px] mx-auto pt-8 md:pt-16 space-y-6">
          <UserData
            profile={profile}
            gearpalsData={{ length: totalListingsCount } as any}
          />
          <UserAddress gearpalsData={gearpalsData || []} />
          <div className="profile-card flex flex-col">
            <GearHeadings variant="h3" className="text-secondary mb-4">
              {t(
                "pages.profile.addYourContributions",
                "Aggiungi il tuo contributo",
              )}
            </GearHeadings>
            <div className="flex justify-start mb-8">
              <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
            </div>

            {activeTab === "gear" ? (
              <>
                {/* Form per aggiungere Gear Items */}
                {gearpalsData && gearpalsData.length > 0 ? (
                  <AddGearItemForm gearpalId={gearpalsData[0].id} />
                ) : (
                  <div className="flex flex-col items-center gap-4 py-8 bg-[#2C1A4A]/30 rounded-2xl border border-white/5 mx-auto">
                    <p className="text-white/60 text-center px-4">
                      {t(
                        "pages.profile.addAddressPrompt",
                        "Per iniziare ad aggiungere la tua attrezzatura, devi prima inserire il tuo indirizzo.",
                      )}
                    </p>
                    <GearButton
                      variant="secondary"
                      onClick={() => openUserAddressModal(false)}
                      className="!rounded-full font-bold shadow-lg shadow-secondary/20 hover:shadow-secondary/30 transition-all active:scale-95"
                    >
                      {t("forms.addAddress", "Aggiungi Indirizzo")}
                    </GearButton>
                  </div>
                )}
                {/* Sezione Gear Items separata */}
              </>
            ) : (
              <AddLivePalForm />
            )}
          </div>
          {activeTab === "gear" ? (
            <UserGearItems
              gearpalsData={gearpalsData || []}
              onEditItem={setEditingItem}
              onDeleteItem={(id) => {
                if (window.confirm(t("common.confirmDelete"))) {
                  deleteItemMutation(id);
                }
              }}
              onViewItem={(item, pal) => {
                setViewingPal(pal);
                setViewingItem(item);
              }}
            />
          ) : (
            <UserVenues
              livepalsData={livepalsData || []}
              onEdit={setEditingVenue}
            />
          )}
        </div>
      </main>

      {editingItem && (
        <EditGearItemModal
          isOpen={true}
          onClose={() => setEditingItem(null)}
          item={editingItem}
        />
      )}

      {editingVenue && (
        <AddOrEditPalForm
          isOpen={true}
          onClose={() => setEditingVenue(null)}
          palType="LivePal"
          isEdit={true}
          palToEdit={editingVenue}
          onSubmitSuccess={() => {
            setEditingVenue(null);
          }}
        />
      )}

      {viewingPal && (
        <PalPopup
          pal={viewingPal}
          initialSelectedItem={viewingItem}
          onClose={() => {
            setViewingPal(null);
            setViewingItem(null);
          }}
        />
      )}

      <Footer />
    </div>
  );
};

export default Profilo;
